import React from 'react'
import Menu, { MenuProps } from 'components/menu'
import { Spreadsheet, Process, Template, Folder } from 'components/icons'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import SidebarMenuList, { SidebarMenuItem } from 'components/sidebar/menuItems'

/** What the sidebar's create options do. Each takes where to create: a folder's id, or null for the top level. */
export interface CreateActions {
  newFolder: (parentId: string | null) => void
  newDocument: (folderId: string | null) => void
  newTable: (folderId: string | null) => void
  newFromTemplate: (folderId: string | null) => void
}

export interface CreateMenuOptions {
  /** Where things are created: a folder's id, or null for the hub's top level. */
  folderId: string | null
  /** Hub Owners create folders; anyone else is shown New folder disabled. */
  canManage: boolean
  /** Owners and Builders create documents and tables. */
  canCreate: boolean
  /** Another level of folder fits here (MAX_HUB_FOLDER_DEPTH); New subfolder is left out where it doesn't. */
  folderFits: boolean
  /** Starts each row's test id, so two menus showing the rows can be told apart. */
  testIdPrefix: string
  actions: CreateActions
  /** Runs before the chosen action: closes the menu the rows are in. */
  close: () => void
}

export const ONLY_OWNERS_CREATE_FOLDERS = 'Only hub Owners can create folders.'

/**
 * The create options, for the hub's + and for a folder's + and right-click menu alike, so the
 * places can't drift: New folder (New subfolder inside a folder), and New document, New table and
 * New from template. Only the top level's rows carry the product tours' hooks, which must be unique.
 */
export const createMenuItems = ({
  folderId,
  canManage,
  canCreate,
  folderFits,
  testIdPrefix,
  actions,
  close
}: CreateMenuOptions): { folder: SidebarMenuItem | null; resources: SidebarMenuItem[] } => {
  const topLevel = folderId === null
  const choose = (action: (folderId: string | null) => void) => () => {
    close()
    action(folderId)
  }
  const folder: SidebarMenuItem | null = folderFits
    ? {
        testId: `${testIdPrefix}-new-${topLevel ? 'folder' : 'subfolder'}`,
        dataTour: topLevel ? 'create-new-folder' : undefined,
        icon: <Folder />,
        label: topLevel ? 'New folder' : 'New subfolder',
        disabledReason: canManage ? undefined : ONLY_OWNERS_CREATE_FOLDERS,
        onSelect: choose(actions.newFolder)
      }
    : null
  const resources: SidebarMenuItem[] = canCreate
    ? [
        {
          testId: `${testIdPrefix}-new-document`,
          dataTour: topLevel ? 'create-new-document' : undefined,
          icon: <Process />,
          label: 'New document',
          onSelect: choose(actions.newDocument)
        },
        {
          testId: `${testIdPrefix}-new-table`,
          dataTour: topLevel ? 'create-new-table' : undefined,
          icon: <Spreadsheet />,
          label: 'New table',
          onSelect: choose(actions.newTable)
        },
        {
          testId: `${testIdPrefix}-from-template`,
          dataTour: topLevel ? 'create-from-template' : undefined,
          icon: <Template />,
          label: 'New from template',
          onSelect: choose(actions.newFromTemplate)
        }
      ]
    : []
  return { folder, resources }
}

type ResourceMenuProps = MenuProps & Omit<CreateMenuOptions, 'testIdPrefix' | 'close'>

/** The + menu: what can be created at the hub's top level, or inside the folder whose + opened it. */
const ResourceMenu: React.FC<ResourceMenuProps> = ({ id, menuState, setMenuState, width, ...options }) => {
  const close = (): void => setMenuState(INITIAL_CONTEXT_MENU_STATE)
  const { folder, resources } = createMenuItems({ ...options, testIdPrefix: 'resource-menu', close })

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width}>
      <SidebarMenuList
        label={options.folderId === null ? 'New' : 'New in folder'}
        items={folder ? [folder, ...resources] : resources}
        onClose={close}
      />
    </Menu>
  )
}

export default ResourceMenu
